import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
}

const mapStateToProps = (
    state: ApplicationState,
    props: { configurationCompleted?: boolean; variantSelectionCompleted?: boolean } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

export interface StickyCartProductQuantityOrderedStyles {
    quantityContainer?: InjectableCss;
    inputFieldStyles?: InjectableCss;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const stickyCartProductQuantityOrderedStyles: StickyCartProductQuantityOrderedStyles = {
    quantityContainer: {
        css: css`
            display: flex;
            border-radius: 13.5px;
            border: solid 2px #7c93ad;
            position: relative;
            width: auto;
            max-width: 88px;
    padding: 0 6px;
    margin-right: 17px;
            > ${FormFieldStyle} {
                > ${FormInputWrapper} {
                    margin-top: 0px !important;
                    input[type="number"]::-webkit-outer-spin-button,
                    input[type="number"]::-webkit-inner-spin-button {
                        /* ISC-11023 deal with this. */
                        /* stylelint-disable-next-line */
                        -webkit-appearance: none;
                        margin: 0;
                    }
                }
            }
            button {
                border: 0px;
                background: none;
                font-size: 21px;
                padding: 0px 4px;                
            }
        `,
    },
    inputFieldStyles: {
        css: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield !important;
            text-align: center;
            border: none !important;
            font-weight: bold;
            padding: 10px 0px 10px 0px !important;
            font-size: 16px !important;
            &:focus {
                border: none !important;
                outline: none !important;
                box-shadow: none !important;
            }
        `,
    },
};

const styles = stickyCartProductQuantityOrderedStyles;

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    ...otherProps
}: Props) => {
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const [initialRender, setInitialRender] = useState(true);

    useEffect(() => {
        let qty = qtyOrdered.toString();
        if (initialRender && properties && properties.minimuM_QTY) {
            qty = properties.minimuM_QTY;
            setInitialRender(false);
            const possibleNumber = Number(qty);
            if (!Number.isNaN(possibleNumber) && onQtyOrderedChanged) {
                onQtyOrderedChanged(possibleNumber);
            }
        }
        setProductQty(`${qty || ""}`);
    }, [qtyOrdered, properties]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        const regex = /^[0-9]*$/;
        if (regex.test(value)) {
            setProductQty(value);
        }

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    const increaseQty = () => {
        let minQty = 1;
        let qty = Number(productQty);
        if (
            typeof properties["minimuM_QTY"] != "undefined" &&
            properties["minimuM_QTY"] &&
            parseInt(properties["minimuM_QTY"]) != 0
        ) {
            minQty = Number(properties["minimuM_QTY"]);
        }
        let modQty = Number(productQty) % minQty;

        if (modQty !== 0) {
            qty = Number(productQty) - modQty;
        }

        let increasedQty = minQty + qty * 1;
        if (increasedQty < 99999) {
            qty = minQty;
        }
        setProductQty(increasedQty.toString());

        if (onQtyOrderedChanged) {
            onQtyOrderedChanged(increasedQty);
        }
    };

    const decreaseQty = () => {
        let minQty = 1;
        let qty = parseInt(productQty);
        if (
            typeof properties["minimuM_QTY"] != "undefined" &&
            properties["minimuM_QTY"] &&
            parseInt(properties["minimuM_QTY"]) != 0
        ) {
            minQty = parseInt(properties["minimuM_QTY"]);
        }
        let modQty: number = parseInt(productQty) % minQty;

        if (modQty !== 0) {
            qty = parseInt(productQty) - modQty;
        } else {
            qty = qty * 1 - minQty;
        }

        if (qty < minQty) {
            qty = minQty;
        }
        setProductQty(qty.toString());

        if (onQtyOrderedChanged) {
            onQtyOrderedChanged(qty);
        }
    };

    return (
        <StyledWrapper {...styles.quantityContainer}>
            <button style={{ display: "flex", alignItems: "center" }} onClick={decreaseQty}>
                <Icon size={18} src="Minus" />
            </button>
            <TextField
                value={productQty}
                type="text"
                min={0}
                max={999999}
                maxLength={5}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles.inputFieldStyles}
            />
            <button style={{ display: "flex", alignItems: "center" }} onClick={increaseQty}>
                <Icon size={18} src="Plus" />
            </button>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
